import { fallback } from "@tanstack/router-zod-adapter";
import { z } from "zod";
import { DataIdSchema } from "./auth";
import { MESSAGE_TAG } from "./message";
import { TeamPageSearchSchema } from "./team";

export const ChatListSearchSchema = TeamPageSearchSchema.pick({
	q: true,
});

export type ChatListSearchSchema = z.infer<typeof ChatListSearchSchema>;

export const MEMBER_ALERT_SCOPE = {
	MY_OUTSTANDING: "my_outstanding",
	AWAITING_RESPONSE: "awaiting_response",
} as const;

export const InboxAlertsSearchParamSchema = z.object({
	q: z.string().optional(),
	tab: fallback(z.nativeEnum(MEMBER_ALERT_SCOPE), "my_outstanding").default(
		"my_outstanding",
	),
});

export type InboxAlertsSearchParamSchema = z.infer<
	typeof InboxAlertsSearchParamSchema
>;

export const InboxRemindersSearchParamSchema = z.object({
	q: z.string().optional(),
});

export type InboxRemindersSearchParamSchema = z.infer<
	typeof InboxRemindersSearchParamSchema
>;

export const BasePaginationParamsSchema = z.object({
	page: z.number().int().optional(),
	limit: z.number().int().optional(),
});

export type BasePaginationParamsSchema = z.infer<
	typeof BasePaginationParamsSchema
>;

export const MessageDetailSearchParams = z.object({
	sender: z.string().optional(),
	cid: z.number().int(),
	"file-idx": z.number().int().optional(),
});

export type MessageDetailSearchParams = z.infer<
	typeof MessageDetailSearchParams
>;

export const InviteCaseMemberSearchParamSchema = z.object({
	tab: fallback(z.enum(["contact", "directory", "mpis"]), "contact").default(
		"contact",
	),
	subtab: fallback(z.enum(["team", "contact"]), "team").default("team"),
	q: z.string().optional(),
	member_id: DataIdSchema.optional(),
	tags: z.nativeEnum(MESSAGE_TAG).optional(),
});

export type InviteCaseMemberSearchParamSchema = z.infer<
	typeof InviteCaseMemberSearchParamSchema
>;

export const InviteCaseMemberExternalSearchParamSchema =
	InviteCaseMemberSearchParamSchema.omit({
		subtab: true,
	});

export type InviteCaseMemberExternalSearchParamSchema = z.infer<
	typeof InviteCaseMemberExternalSearchParamSchema
>;

export const RootRouteSearchSchema = z.object({
	token: z.string().trim().optional(),
	"access-token": z.string().optional(),
	client: z.string().optional(),
	uid: z.string().optional(),
	is_mobile: z.boolean().optional(),
	have_cases: z.boolean().optional(),
	encounter_id: DataIdSchema.optional(),
	patient_ic_number: z.coerce.string().trim().optional(),
});
export type RootRouteSearchSchemaType = z.infer<typeof RootRouteSearchSchema>;

export const GalleryCameraSearchSchema = z.object({
	tag: z.nativeEnum(MESSAGE_TAG).optional(),
});
export type GalleryCameraSearchSchema = z.infer<
	typeof GalleryCameraSearchSchema
>;

export const GalleryVideoSearchSchema = GalleryCameraSearchSchema;
export type GalleryVideoSearchSchema = z.infer<typeof GalleryVideoSearchSchema>;

export const GalleryViewSearchSchema = GalleryCameraSearchSchema;
export type GalleryViewSearchSchema = z.infer<typeof GalleryViewSearchSchema>;

export const SearchMemberTeamSearchSchema = z.object({
	q: z.string().optional(),
	type: fallback(
		z.enum(["users", "pending_invitations"]).default("users"),
		"users",
	),
});
export type SearchMemberTeamSearchSchema = z.infer<
	typeof SearchMemberTeamSearchSchema
>;

export const AddTeamMemberSearchSchema = z.object({
	q: z.string().trim().optional(),
});
export type AddTeamMemberSearchSchema = z.infer<
	typeof AddTeamMemberSearchSchema
>;

export const CaseExistPatientSearchSchema = z.object({
	encounter_id: DataIdSchema.optional(),
	have_cases: z.boolean().optional(),
	reference: z.string().optional(),
});
export type CaseExistPatientSearchSchema = z.infer<
	typeof CaseExistPatientSearchSchema
>;

export const InboxAlertSearchSchema = z.object({
	id: DataIdSchema.optional(),
	type: z.enum(["Team", "Group", "Case"]).optional(),
	action: z.enum(["accept", "reject"]).optional(),
});
export type InboxAlertSearchSchema = z.infer<typeof InboxAlertSearchSchema>;

export const CreateGroupSearchParamSchema = z.object({
	directory: z.boolean().optional(),
});
export type CreateGroupSearchParamSchema = z.infer<
	typeof CreateGroupSearchParamSchema
>;

export const CreateTeamSearchParamSchema = CreateGroupSearchParamSchema;
export type CreateTeamSearchParamSchema = z.infer<
	typeof CreateTeamSearchParamSchema
>;

export const CreateTeamIndexSearchParamSchema = z.object({
	q: z.string().trim().optional(),
});
export type CreateTeamIndexSearchParamSchema = z.infer<
	typeof CreateTeamIndexSearchParamSchema
>;

export const CreateCaseSearchParamSchema = z.object({
	step: z.number().optional(),
	pid: DataIdSchema.nullish(),
	pdob: z.string().nullish(),
	psex: z.string().nullish(),
	pname: z.string().nullish(),
	psurname: z.string().nullish(),
	paddress: z.string().nullish(),
	pic: z.string().nullish(),
	cid: DataIdSchema.nullish(),
	encounter_id: DataIdSchema.nullish(),
});
export type CreateCaseSearchParamSchema = z.infer<
	typeof CreateCaseSearchParamSchema
>;

export const RequestAccessCaseSearchParamSchema = CreateCaseSearchParamSchema;
export type RequestAccessCaseSearchParamSchema = z.infer<
	typeof RequestAccessCaseSearchParamSchema
>;

export const CameraSearchParamSchema = z.object({
	action: fallback(z.enum(["photo", "video"]), "photo").default("photo"),
	tag: z.nativeEnum(MESSAGE_TAG).optional(),
});
export type CameraSearchParamSchema = z.infer<typeof CameraSearchParamSchema>;

export const CaseDetailSearchParamSchema = z.object({
	tab: fallback(
		z.enum(["patient", "medical_notes", "case_summary", "letters", "members"]),
		"patient",
	).default("patient"),
});
export type CaseDetailSearchParamSchema = z.infer<
	typeof CaseDetailSearchParamSchema
>;
// InviteCaseMemberSearchParamSchema
export const ReferralSuccessSearchParamSchema = z.object({
	cid: DataIdSchema,
	members: z.string(),
	ctitle: z.string(),
	tags: z.string(),
});
export type ReferralSuccessSearchParamSchema = z.infer<
	typeof ReferralSuccessSearchParamSchema
>;

export const ReferralMemberSearchParamSchema =
	InviteCaseMemberSearchParamSchema.omit({
		member_id: true,
	});
export type ReferralMemberSearchParamSchema = z.infer<
	typeof ReferralMemberSearchParamSchema
>;
